import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

/**
 * Feb 2026 — cookie migration.
 * The JWT now lives in an httpOnly cookie the browser handles automatically.
 * The old `sukana_token` in localStorage is only removed on logout so
 * previously-logged-in tabs stay authenticated until they log back in.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Seed the CSRF cookie unconditionally — it lets us submit `X-CSRF-Token`
        // on the very first mutation even if the user isn't logged in yet.
        await api.get("/auth/csrf").catch(() => {});
        const { data } = await api.get("/auth/me");
        if (!cancelled) setUser(data);
      } catch {
        // No valid session — silent
        localStorage.removeItem("sukana_token");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // The JWT is now in a cookie — clear any legacy value.
    localStorage.removeItem("sukana_token");
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.removeItem("sukana_token");
    setUser(data.user);
    return data;
  }, []);

  const logout = useCallback(async () => {
    // Best-effort server-side cookie clear; local state still wipes even if
    // the request fails (offline, expired session, etc.).
    try { await api.post("/auth/logout"); } catch { /* silent */ }
    localStorage.removeItem("sukana_token");
    sessionStorage.removeItem("sukana_token");
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, setUser }),
    [user, loading, login, register, logout]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
